import { AICommandResult, Command, ICommandCanExecuteReturnValue, ICommandContext, UndoableCommand } from "../../commands/common/commands.js";
import { HierarchyNode } from "../../hierarchy/common/hierarchyService.js";
import { IProperty, IPropertyGroup, IPropertyService, isPropertyAddress, isPropertyLike, PropertyAddress, ValueMetadata } from "./properties.js";
import { ILoggerService, ILogService } from "../../log/common/log.js";
import { IInstantiationService, ServicesAccessor } from "../../instantiation/common/instantiation.js";
import { IChangesetService } from "../../../editor/services/changeset/common/changeset.js";
import { safeStringify } from "../../../base/common/objects.js";
import { IClipboardService } from "../../clipboard/common/clipboardService.js";
import { INotificationService } from "../../notifications/common/notifications.js";
import { IAnalyticsService } from "../../analytics/common/analytics.js";
import { ContextMenuContext, isContextMenuContext } from "../../contextview/common/context.js";
import { MenuId, MenuRegistry } from "../../actions/common/actions.js";
// #region SetPropertyCommand

export type ISetPropertyCommandArgs = {
	property: IProperty;
	value: any;
	previousValue?: any;
	metadata?: ValueMetadata;
	/**
	 * Hint for value conversion, e.g., "skybox" for HDRi textures that need equirectangular mapping.
	 * Passed through to accessors/converters to guide how values are processed.
	 */
	usageHint?: "skybox" | string;
}

type SetPropertyParametersForAI = Omit<ISetPropertyCommandArgs, 'property'> & {
	objectId: string | number;
	propertyPath: string;
}
function isSetPropertyParametersForAI(obj: any): obj is SetPropertyParametersForAI {
	return obj && typeof obj === "object"
		&& 'objectId' in obj && (typeof obj.objectId === "string" || typeof obj.objectId === "number")
		&& 'propertyPath' in obj && typeof obj.propertyPath === "string"
		&& 'value' in obj;
}

/**
 * Command to set a property value.
 * Ensures changes propagate to TreeNode and the UI.
 */
export class SetPropertyCommand extends UndoableCommand<ISetPropertyCommandArgs> {
	static readonly ID = 'property.setValue';

	constructor(
		// private readonly logger: ILogService,
		// private readonly propertyService: IPropertyService,
	) {
		super(SetPropertyCommand.ID, 'Set Property', {
			description: 'Set an object property value',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The property and value to set',
					properties: {
						objectId: {
							type: 'string',
							description: 'The ID of the object containing the property'
						},
						propertyPath: {
							type: 'string',
							description: 'The path/name of the property to modify. If nested use . to separate levels. If array index, use [index]. E.g., "transform.position.x" or "materials[0].color"'
						},
						value: {
							// Removing type restriction to allow any value due to issue in vscode and cursor where union types fail https://linear.app/needle/issue/NE-6875
							// type: ['string', 'number', 'boolean', 'object', 'array', 'null'],
							description: 'The new value for the property can be of any type'
						},
						previousValue: {
							// Removing type restriction to allow any value due to issue in vscode and cursor where union types fail https://linear.app/needle/issue/NE-6875
							// type: ['string', 'number', 'boolean', 'object', 'array', 'null'],
							description: 'The previous value for the property (optional)'
						}
					},
					required: ['objectId', 'propertyPath', 'value']
				}
			},
			returns: 'void',
		});
	}

	canExecute(args: ISetPropertyCommandArgs | SetPropertyParametersForAI): ICommandCanExecuteReturnValue {

		if (isSetPropertyParametersForAI(args)) {
			return {
				canExecute: true
			}
		}

		if (!args.property) {
			return {
				canExecute: false,
				reason: 'No property provided'
			};
		}
		if (!isPropertyLike(args.property)) {
			return {
				canExecute: false,
				reason: 'Invalid property object. Expected IProperty.'
			};
		}
		if (!args.property.editable) {
			return {
				canExecute: false,
				reason: `Property ${args.property.key} is not editable`
			};
		}
		return {
			canExecute: true
		};
	}

	private oldValue?: any;

	protected async perform(accessor: ServicesAccessor, args: ISetPropertyCommandArgs | SetPropertyParametersForAI, context?: ICommandContext, isUndo?: boolean): Promise<IProperty> {

		const propertyService = accessor.get(IPropertyService);
		const logger = accessor.get(ILogService);
		const changesService = accessor.getIfExists(IChangesetService);

		if (isSetPropertyParametersForAI(args)) {
			const inst = accessor.get(IInstantiationService);
			const result = await propertyService.findProperty({
				objectId: String(args.objectId),
				propertyPath: args.propertyPath
			});
			if (!result) {
				throw new Error(`Property at path ${args.propertyPath} not found on object with ID ${args.objectId}`);
			}
			return inst.invokeFunction(a => {
				return this.perform(a, {
					property: result,
					value: args.value,
					previousValue: args.previousValue,
					metadata: args.metadata,
					usageHint: args.usageHint
				});
			});
		}

		const property = args.property;
		const value = args.value;

		if (!isPropertyLike(property)) {
			throw new Error('Invalid property object. Expected IProperty.');
		}

		// Store old value for undo
		if (!isUndo) {
			if (args.previousValue !== undefined) {
				this.oldValue = args.previousValue;
			}
			else {
				this.oldValue = property.cloneValue();
			}

			// Don't do anything if value hasn't changed
			// TODO: comparison for complex types
			if (property.equalsValue(value, this.oldValue)) {
				logger.debug(`SetPropertyCommand: Value for property ${property.key} unchanged, skipping.`, typeof value !== "object" ? value : "");
				return property;
			}
		}

		const prev = this.oldValue;
		return property.setValue({
			newValue: value,
			withoutNotify: true,
			metadata: args.metadata,
			usageHint: args.usageHint
		}).then(_ => {
			property.notifyChanged({ value: true, previousValue: prev });
			changesService?.registerPropertyChange(property, prev);

			// Sync TreeNode if this object has one
			if (property.key === 'name') {
				const owner = property.owner;
				const node = HierarchyNode.get(owner);
				if (node) {
					node.name = value;
				}
			}
			return property;
		});
	}

	executeForAI(accessor: ServicesAccessor, args: ISetPropertyCommandArgs, context?: ICommandContext): Promise<string> {
		return this.perform(accessor, args, context).then(property => {
			return `Property '${property?.metadata.address ?? property.key}' set successfully.`;
		});
	}


	protected undo(accessor: ServicesAccessor, args: ISetPropertyCommandArgs, context?: ICommandContext): void {
		this.perform(accessor, {
			...args,
			// swap value and previousValue
			value: this.oldValue,
			previousValue: args.value
		}, context, true);
	}
}


// #region CopyPropertyGroupCommand

export interface CopyPropertyGroupCommandArgs {
}

export class CopyPropertyGroupCommand extends Command<CopyPropertyGroupCommandArgs> {
	static readonly ID = 'property.group.copy';

	constructor() {
		super(CopyPropertyGroupCommand.ID, 'Copy Property Group as JSON', {
			description: 'Copy all objects in a property group to clipboard as JSON',
		});
	}

	canExecute(args: unknown) {
		return { canExecute: true };
	}

	async execute(accessor: ServicesAccessor, args: unknown) {
		const logger = accessor.get(ILogService);
		const clipboard = accessor.get(IClipboardService);
		const notification = accessor.get(INotificationService);
		const telemetry = accessor.get(IAnalyticsService);

		let group: IPropertyGroup | null = null;

		if (isContextMenuContext(args)) {
			telemetry.logEvent('property.group.copy', { source: 'contextMenu' });
			// Find the property group in the targets
			const target = args.targets?.find(t => t && typeof t === 'object' && 'properties' in t && 'id' in t);
			if (target) {
				group = target as IPropertyGroup;
			} else {
				logger.error('No property group found in context menu targets.');
				notification.showNotification({
					key: 'copyPropertyGroupNoGroup',
					type: 'error',
					message: 'Cannot copy: No property group found.',
					position: 'cursor'
				});
				return;
			}
		}

		if (!group) {
			logger.error('No property group provided.');
			notification.showNotification({
				type: 'error',
				message: 'Cannot copy: No property group provided.'
			});
			return;
		}

		try {
			// Serialize all property values in the group
			const groupData: any = {
				groupName: group.name,
				groupId: group.id,
				properties: {}
			};

			for (const property of group.properties) {
				if (!property.hidden) {
					try {
						groupData.properties[property.key] = {
							value: property.value,
							type: property.type,
							label: property.label
						};
					} catch (err) {
						logger.warn(`Failed to read property ${property.key}:`, err);
					}
				}
			}

			const json = safeStringify(groupData);
			await clipboard.writeText(json);

			logger.info(`Copied property group "${group.name}" to clipboard.`);
			notification.showNotification({
				type: 'info',
				message: `Copied property group "${group.name}" to clipboard.`,
				position: 'cursor',
			});
		} catch (err) {
			telemetry.logEvent('property.group.copy.failed', {
				reason: 'exception',
				message: (err instanceof Error ? err.message : String(err)).substring(0, 500)
			});
			logger.error('Failed to copy property group to clipboard.', err);
			notification.showNotification({
				type: 'error',
				message: 'Failed to copy property group to clipboard: ' + (err instanceof Error ? err.message : String(err)).substring(0, 200),
				position: 'toast',
			});
		}
	}
}

MenuRegistry.appendMenuItem(MenuId.InspectorGroupHeaderContext, {
	command: {
		id: CopyPropertyGroupCommand.ID,
		title: 'Copy as JSON',
		icon: { id: 'copy' }
	},
	when: undefined,
	order: 10,
	group: '10_clipboard',
});


